// Cookie consent store (spec 2026-09-14-meta-ads-end-to-end-tracking-design §4.1).
// Consent regions (EU/EEA, UK, CH and territories): nothing optional loads until
// the visitor chooses in the banner. Everywhere else a default record (analytics
// on, marketing on unless Global Privacy Control) is written immediately, and the
// footer "Your privacy choices" link lets people opt out.
// The record lives in the w369_consent cookie on .workspace369.com so the app
// and backend read the same decision; localStorage mirrors it.
// The load-time merge/expiry/rewrite logic is a pure function (resolveConsentOnLoad)
// tested in tests/tracking/consentRecord.test.ts — this composable only wires DOM
// (cookies, localStorage, navigator.globalPrivacyControl) to it.
import {
  CONSENT_COOKIE,
  CONSENT_MAX_AGE_SEC,
  GEO_COOKIE,
  adConsentOf,
  buildCookie,
  readCookie,
  resolveConsentOnLoad,
  serializeConsent,
  type AdConsent,
  type ConsentRecord,
} from '~/utils/tracking/consentRecord';

const STORAGE_KEY = 'w369_consent';

export const useCookieConsent = () => {
  const consent = useState<ConsentRecord | null>('w369-cookie-consent', () => null);
  const consentRequired = useState<boolean>('w369-consent-required', () => false);
  const country = useState<string>('w369-consent-country', () => 'XX');
  const gpc = useState<boolean>('w369-consent-gpc', () => false);
  const preferencesOpen = useState<boolean>('w369-consent-preferences-open', () => false);
  const loaded = useState<boolean>('w369-consent-loaded', () => false);

  const gpcEnabled = () =>
    import.meta.client &&
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true;

  const secure = () => window.location.protocol === 'https:';

  // Cookie + localStorage mirror only — no state assignment. A `write` action
  // from resolveConsentOnLoad can be for a record that's merely *hidden* this
  // load (e.g. an aged opt-out refreshed, or a healed mirror while the visitor
  // is outside its region): the visible `consent` must never flash that
  // record's grants, so load() persists without touching state and assigns
  // `consent.value` once at the end, from the resolved result alone.
  const persist = (record: ConsentRecord, maxAgeSec: number) => {
    if (!import.meta.client) return;
    const value = serializeConsent(record);
    document.cookie = buildCookie(CONSENT_COOKIE, value, maxAgeSec, window.location.hostname, secure());
    try {
      window.localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // storage blocked — the cookie is the source of truth
    }
  };

  const write = (record: ConsentRecord, maxAgeSec: number = CONSENT_MAX_AGE_SEC) => {
    consent.value = record;
    persist(record, maxAgeSec);
  };

  // Removes a stored record everywhere (shared cookie and local mirror) so neither
  // this site, the app nor the backend can read it as consent.
  const forget = () => {
    consent.value = null;
    if (!import.meta.client) return;
    document.cookie = buildCookie(CONSENT_COOKIE, '', 0, window.location.hostname, secure());
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      // storage blocked
    }
  };

  const load = () => {
    if (!import.meta.client || loaded.value) return;
    loaded.value = true;

    let storedValue: string | null = null;
    try {
      storedValue = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      storedValue = null;
    }

    const gpcOn = gpcEnabled();
    gpc.value = gpcOn;

    const result = resolveConsentOnLoad({
      cookieHeader: document.cookie,
      storedValue,
      geoCookie: readCookie(document.cookie, GEO_COOKIE),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      gpc: gpcOn,
      nowSec: Math.floor(Date.now() / 1000),
      hostname: window.location.hostname,
    });

    consentRequired.value = result.region.consentRequired;
    country.value = result.region.country;

    for (const action of result.actions) {
      if (action.type === 'expireLegacyHostCookie') {
        // A legacy host-only w369_consent (the old banner wrote "decided") can shadow
        // the shared cookie. Expiring it without a Domain attribute leaves the
        // .workspace369.com cookie untouched.
        document.cookie = `${CONSENT_COOKIE}=; Path=/; Max-Age=0`;
      } else if (action.type === 'forget') {
        forget();
      } else if (action.type === 'write') {
        persist(action.record, action.maxAgeSec);
      }
    }

    // The resolved result is the single source of truth for this load.
    consent.value = result.consent;
  };

  const decide = (analytics: boolean, marketing: boolean) => {
    const gpcOn = gpcEnabled();
    gpc.value = gpcOn;
    write({
      analytics,
      marketing: marketing && !gpcOn,
      region: country.value,
      gpc: gpcOn,
      decided: true,
      decidedAtSec: Math.floor(Date.now() / 1000),
    });
    preferencesOpen.value = false;
  };

  const acceptAll = () => decide(true, true);
  const rejectNonEssential = () => decide(false, false);
  const save = (prefs: { analytics: boolean; marketing: boolean }) =>
    decide(Boolean(prefs.analytics), Boolean(prefs.marketing));
  const openPreferences = () => {
    preferencesOpen.value = true;
  };
  const closePreferences = () => {
    preferencesOpen.value = false;
  };

  const needsBanner = computed(() => consentRequired.value && !consent.value?.decided);
  const adConsent = computed<AdConsent>(() => adConsentOf(consent.value));

  return {
    consent,
    gpc,
    preferencesOpen,
    needsBanner,
    adConsent,
    load,
    acceptAll,
    rejectNonEssential,
    save,
    openPreferences,
    closePreferences,
  };
};
